import { Glass, Dot, ScreenLabel, BodyText, alpha } from "../../components/mobile/primitives";

export const CAPTURE_SUGGESTIONS = [
  {
    dot: "var(--color-kin-gold)",
    title: "Log your mood",
    sub: "Tracker \u00b7 streak 4 days",
  },
  {
    dot: "var(--color-kin-green-soft)",
    title: "Upload blood work",
    sub: "Sleep Deep Dive needs this",
  },
  {
    dot: "var(--color-kin-lavender)",
    title: "Continue diet log",
    sub: "Last logged 2h ago",
  },
];

export const CAPTURE_MODES = [
  { id: "note", label: "Note", active: true },
  { id: "voice", label: "Voice", active: false },
  { id: "photo", label: "Photo", active: false },
  { id: "file", label: "File", active: false },
  { id: "scan", label: "Scan", active: false },
];

function ModeIcon({ id, color }) {
  const c = color;
  switch (id) {
    case "note":
      return (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke={c}
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 4h12v16H6z" />
          <path d="M9 8h6" />
          <path d="M9 12h4" />
        </svg>
      );
    case "voice":
      return (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke={c}
          strokeWidth="1.8"
          strokeLinecap="round"
        >
          <rect x="9" y="4.5" width="6" height="11" rx="3" />
          <path d="M6.5 11.5v1a5.5 5.5 0 0 0 11 0v-1" />
          <path d="M12 18v2.5" />
        </svg>
      );
    case "photo":
      return (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke={c}
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3.5" y="6.5" width="17" height="12" rx="2" />
          <circle cx="12" cy="12.5" r="3.3" />
          <path d="M8.2 6.5 9.4 4.6h5.2l1.2 1.9" />
        </svg>
      );
    case "file":
      return (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke={c}
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
      );
    case "scan":
      return (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke={c}
          strokeWidth="1.8"
          strokeLinecap="round"
        >
          <path d="M7 4H5.5A1.5 1.5 0 0 0 4 5.5V7" />
          <path d="M17 4h1.5A1.5 1.5 0 0 1 20 5.5V7" />
          <path d="M20 17v1.5A1.5 1.5 0 0 1 18.5 20H17" />
          <path d="M7 20H5.5A1.5 1.5 0 0 1 4 18.5V17" />
          <path d="M8 12h8" />
        </svg>
      );
    default:
      return null;
  }
}

export function CaptureScreen() {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Dimmed backdrop */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0,0,0,0.3)",
          zIndex: 0,
        }}
      />

      {/* Spacer to push sheet down — sheet takes ~60% from bottom */}
      <div style={{ flex: "0 0 40%", zIndex: 1 }} />

      {/* Bottom sheet */}
      <div
        style={{
          flex: 1,
          zIndex: 2,
          display: "flex",
          flexDirection: "column",
          background:
            "linear-gradient(180deg, rgba(248,245,238,0.98) 0%, rgba(245,243,236,0.99) 100%)",
          borderRadius: "24px 24px 0 0",
          boxShadow: "0 -8px 32px -8px rgba(0,0,0,0.12), 0 -2px 8px -2px rgba(0,0,0,0.04)",
          overflow: "hidden",
        }}
      >
        {/* Drag handle */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "10px 0 6px",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 40,
              height: 5,
              borderRadius: 3,
              background: "var(--color-kin-divider)",
            }}
          />
        </div>

        {/* Input bar */}
        <div style={{ padding: "4px 16px 0", flexShrink: 0 }}>
          <Glass
            style={{
              padding: "8px 10px 8px 16px",
              display: "flex",
              alignItems: "center",
              gap: 10,
              borderRadius: 28,
              boxShadow: "0 4px 16px -4px rgba(0,0,0,0.06)",
            }}
          >
            <span
              style={{
                flex: 1,
                fontSize: 14,
                fontWeight: 500,
                color: "var(--color-kin-muted)",
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontStyle: "italic",
                lineHeight: 1.3,
              }}
            >
              Capture a moment...
            </span>
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                background: "var(--color-kin-green-soft)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                boxShadow: "0 2px 8px rgba(91,122,102,0.4)",
              }}
            >
              <span
                style={{
                  color: "var(--color-kin-white)",
                  fontSize: 14,
                  fontWeight: 800,
                  lineHeight: 1,
                }}
              >
                ↑
              </span>
            </div>
          </Glass>
        </div>

        {/* Mode selector */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-evenly",
            padding: "14px 12px 10px",
            flexShrink: 0,
          }}
        >
          {CAPTURE_MODES.map((mode) => (
            <div
              key={mode.id}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 4,
              }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  background: mode.active ? "rgba(91,122,102,0.14)" : "transparent",
                  border: mode.active ? "1px solid rgba(91,122,102,0.2)" : "1px solid transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.3s cubic-bezier(0.25, 1, 0.3, 1)",
                }}
              >
                <ModeIcon
                  id={mode.id}
                  color={mode.active ? "var(--color-kin-green-soft)" : "var(--color-kin-muted)"}
                />
              </div>
              <ScreenLabel
                style={{
                  letterSpacing: "0.12em",
                  fontSize: 7,
                  color: mode.active ? "var(--color-kin-green-soft)" : "var(--color-kin-muted)",
                  fontWeight: mode.active ? 800 : 700,
                }}
              >
                {mode.label}
              </ScreenLabel>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div
          style={{
            height: 1,
            background: "rgba(0,0,0,0.04)",
            margin: "0 16px",
            flexShrink: 0,
          }}
        />

        {/* Suggestions section */}
        <div
          style={{
            padding: "12px 16px 16px",
            flex: 1,
            overflowY: "hidden",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <ScreenLabel
            style={{
              letterSpacing: "0.2em",
              paddingLeft: 4,
              marginBottom: 2,
            }}
          >
            Suggestions
          </ScreenLabel>

          {CAPTURE_SUGGESTIONS.map((item, i) => (
            <Glass
              key={i}
              style={{
                padding: "12px 14px",
                borderRadius: 20,
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <Dot
                color={item.dot}
                size={8}
                style={{ boxShadow: `0 0 6px ${alpha(item.dot, "60")}` }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <BodyText size={11} color="var(--color-kin-black)" weight={700}>
                  {item.title}
                </BodyText>
                <BodyText
                  size={9}
                  color="var(--color-kin-muted)"
                  weight={500}
                  style={{ marginTop: 1 }}
                >
                  {item.sub}
                </BodyText>
              </div>
              <span
                style={{
                  color: "var(--color-kin-olive-light)",
                  fontSize: 18,
                  flexShrink: 0,
                  fontWeight: 300,
                }}
              >
                ›
              </span>
            </Glass>
          ))}
        </div>
      </div>
    </div>
  );
}
